import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  unsubscribeReminders,
  unsubscribeByOrgId,
  organizationService,
} from "../../services/api/organization";
import {
  CheckCircle2,
  AlertCircle,
  MailX,
  Loader2,
  ThumbsDown,
  Mailbox,
  Frown,
  Building2,
} from "lucide-react";

const REASONS = [
  {
    id: "frequency",
    label: "I receive too many emails",
    icon: <Mailbox size={18} />,
  },
  {
    id: "relevance",
    label: "The content is not relevant to me",
    icon: <ThumbsDown size={18} />,
  },
  {
    id: "no_longer_need",
    label: "I no longer want to use this service",
    icon: <Frown size={18} />,
  },
  {
    id: "alternative",
    label: "I found a better alternative",
    icon: <Building2 size={18} />,
  },
  { id: "other", label: "Other", icon: <AlertCircle size={18} /> },
];

const Unsubscribe: React.FC = () => {
  const { encodedData } = useParams<{
    encodedData?: string;
  }>();

  const [searchParams] = useSearchParams();
  const orgId = searchParams.get("Id");

  const [orgData, setOrgData] = useState<any>(null);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrg = async () => {
      if (orgId) {
        setLoadingOrg(true);
        try {
          const res = await organizationService.get(orgId);
          if (res.status === "success" && res.data) {
            setOrgData(res.data);
          }
        } catch (err) {
          console.error("Failed to fetch organization details", err);
        } finally {
          setLoadingOrg(false);
        }
      }
    };
    fetchOrg();
  }, [orgId]);

  const handleReasonChange = (reasonLabel: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reasonLabel)
        ? prev.filter((r) => r !== reasonLabel)
        : [...prev, reasonLabel],
    );
  };

  const handleSubmit = async () => {
    if (!encodedData && !orgId) {
      setError("Invalid unsubscribe link.");
      return;
    }

    // if (selectedReasons.length === 0) {
    //   setError("Please select at least one reason.");
    //   return;
    // }

    setLoading(true);
    setError(null);

    try {
      if (orgId && orgData) {
        const email =
          orgData.contactEmail ||
          orgData.email ||
          orgData.companyEmail ||
          orgData.representativeEmail;
        if (!email) {
          setError("No email found for this organization. Cannot unsubscribe.");
          setLoading(false);
          return;
        }
        const payloadToEncode = JSON.stringify({
          organizationId: orgId,
          email: email,
        });
        const base64String = btoa(
          unescape(encodeURIComponent(payloadToEncode)),
        );
        const dynamicEncoded = encodeURIComponent(base64String);
        await unsubscribeReminders(dynamicEncoded);
      } else if (encodedData) {
        await unsubscribeReminders(encodedData);
      } else {
        setError("Invalid unsubscribe parameters.");
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError("Failed to unsubscribe. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 selection:bg-primary/10 relative overflow-hidden font-abel">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-50 rounded-full blur-3xl opacity-60" />

        <div className="max-w-md w-full bg-white/80 backdrop-blur-xl border border-white rounded-[2rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] p-10 text-center animate-in zoom-in-95 duration-500 z-10 relative">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 mb-6 border border-green-100 shadow-sm relative overflow-hidden">
            <CheckCircle2 className="w-10 h-10 text-green-500 z-10 relative" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
            Successfully Unsubscribed
          </h2>
          <p className="text-[15px] leading-relaxed text-gray-500 mb-8 max-w-[280px] mx-auto">
            You've been successfully removed from our mailing list. We're sorry
            to see you go
            {orgData?.companyName ? `, ${orgData?.companyName}` : ""}!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center p-4 selection:bg-primary/10 relative overflow-hidden font-abel">
      {/* Decorative Blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gradient-to-br from-blue-100 to-indigo-50 rounded-full blur-[80px] opacity-70 pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gradient-to-tr from-rose-50 to-orange-50 rounded-full blur-[80px] opacity-70 pointer-events-none" />

      <div className="max-w-xl w-full">
        {/* Main Card */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/80 rounded-[2rem] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.08)] p-8 sm:p-12 relative animate-in slide-in-from-bottom-4 duration-700 z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200/60 shadow-sm mb-6 text-gray-700">
              <MailX size={28} />
            </div>
            {loadingOrg ? (
              <div className="flex justify-center mb-2">
                <div className="h-8 bg-gray-100 rounded-lg w-48 animate-pulse" />
              </div>
            ) : (
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3">
                Unsubscribe
                {orgData?.companyName ? ` ${orgData?.companyName}` : ""}
              </h1>
            )}
            <p className="text-[15px] sm:text-[16px] text-gray-500 max-w-sm mx-auto leading-relaxed">
              We're sorry to see you go. Please let us know why you're leaving
              so we can improve our services.
            </p>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50/80 backdrop-blur-sm border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 animate-in shake duration-300">
              <AlertCircle size={20} className="shrink-0" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <div className="space-y-3 mb-10">
            {REASONS.map((reason) => {
              const isSelected = selectedReasons.includes(reason.label);
              return (
                <label
                  key={reason.id}
                  className={`flex items-center p-4 border rounded-2xl cursor-pointer transition-all duration-200 shadow-sm outline-none w-full ${
                    isSelected
                      ? "border-primary/40 bg-primary/[0.02] shadow-[0_4px_12px_-4px_rgba(0,0,0,0.05)] ring-1 ring-primary/20 scale-[1.01]"
                      : "border-gray-200/70 bg-white hover:bg-gray-50 hover:border-gray-300 active:scale-[0.99]"
                  }`}
                >
                  <div className="relative flex items-center justify-center shrink-0">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={isSelected}
                      onChange={() => handleReasonChange(reason.label)}
                    />
                    <div
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                        isSelected
                          ? "bg-primary border-primary text-white"
                          : "border-gray-300 bg-white text-transparent group-hover:border-gray-400"
                      }`}
                    >
                      <CheckCircle2
                        size={16}
                        strokeWidth={2.5}
                        className={isSelected ? "block" : "hidden"}
                      />
                    </div>
                  </div>
                  <div
                    className={`ml-4 flex items-center gap-3 transition-colors ${isSelected ? "text-primary" : "text-gray-600"}`}
                  >
                    <div
                      className={isSelected ? "text-primary" : "text-gray-400"}
                    >
                      {reason.icon}
                    </div>
                    <span
                      className={`text-[15px] font-medium leading-none mt-0.5 ${isSelected ? "text-gray-900" : "text-gray-700"}`}
                    >
                      {reason.label}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 px-6 bg-gray-900 text-white font-semibold rounded-2xl hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-70 disabled:active:scale-100 disabled:cursor-not-allowed flex justify-center items-center gap-2 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_24px_-8px_rgba(0,0,0,0.4)]"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Confirm Unsubscribe"
            )}
          </button>
        </div>

        {/* Footer info */}
        <p className="text-center text-xs text-gray-400 mt-8 font-medium tracking-wide">
          Changed your mind?{" "}
          <a
            href="/"
            className="text-gray-500 hover:text-gray-800 transition-colors underline underline-offset-4 decoration-gray-300 hover:decoration-gray-600"
          >
            Return to Homepage
          </a>
        </p>
      </div>
    </div>
  );
};

export default Unsubscribe;
